import { performOnlineItemDeactivation } from 'src/brm/actions/authorize/authorize-online-items';
import { IBRMInput } from 'src/common/api/brm/brm-utils';
import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { BrmActionType } from 'src/common/enumerations/brm-action-type';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import {
  CannotVoidTransactionWithTenderLines,
  CartContainCoupons,
  CartContainTendersException,
  OnlineItemVoidAuthorization,
  TransactionNotFound,
} from 'src/common/helpers/error-code';
import { getEJItemBySequenceNumber } from 'src/common/helpers/item-list-helper';
import XLRLogger from 'src/common/helpers/logger';
import { getValueAsNumber } from 'src/common/helpers/number-helper';
import { voidAllCoupons } from 'src/coupons/business-logic/service/coupon-services';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { checkAndGetArray } from '../../common/helpers/array-helper';
import { IReceipt } from '../../common/interfaces/dtos/IReceipt';
import { store } from '../../common/redux/configure-store';
import { ItemListActions } from '../../common/redux/reducers/item-list-reducer';
import { printReceipts } from '../../devices/printer-operations';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { transactionCancel } from './service/transaction-cancel';
import { transactionEnd } from './transaction-end';

export const voidActiveTransaction = async (): Promise<void> => {
  return withSpinner(voidTransaction);
};

const voidTransaction = async (brmInputs?: IBRMInput[]): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  const { voidAllCouponsOnVoidTransaction } = getCurrentXLRConfig();
  const hasCoupon = store
    .getState()
    .itemList.ejItems.find(item => item.Type === EJRecordTypes.Coupon && !item.Voided);

  if (hasCoupon && voidAllCouponsOnVoidTransaction) {
    await voidAllCoupons().catch(() => {});
  }

  if (transactionId) {
    return transactionCancel(transactionId, false, brmInputs)
      .then(
        async (receipts: IReceipt[]): Promise<void> => {
          if (receipts && receipts.length > 0) {
            await printReceipts(checkAndGetArray(receipts)).catch(popupMessage);
          }

          await transactionEnd();
          store.dispatch(ItemListActions.setTransactionVoided(true));
        },
      )
      .catch(
        async (err): Promise<void> => {
          if (err.code === TransactionNotFound) {
            await transactionEnd();
            store.dispatch(ItemListActions.setTransactionVoided(true));
          } else if (err.code === CartContainCoupons) {
            popupMessage(
              new EmeraldErrorModal({
                message: CartContainCoupons,
                type: EmeraldDialogTypes.Error,
                subMessage: 'CartContainCoupons_SubMessage',
                hideCancelButton: true,
              }),
            );
          } else if (err.code === CartContainTendersException) {
            popupMessage(
              new EmeraldErrorModal({
                message: CartContainTendersException,
                type: EmeraldDialogTypes.Info,
                subMessage: 'CartContainTenders_SubMessage',
                hideCancelButton: true,
              }),
            );
          } else if (err.code === OnlineItemVoidAuthorization) {
            const ejItem = getEJItemBySequenceNumber(getValueAsNumber(err?.data?.lineId));
            if (ejItem?.OnlineItem?.authorizationExternalId) {
              const deactivationData = await performOnlineItemDeactivation(ejItem);
              const brmInputs: IBRMInput[] = [
                {
                  ruleId: err.code,
                  lineId: ejItem.SequenceNumber.toString(),
                  type: BrmActionType.OnlineItemAuthorization,
                  restrictionType: OnlineItemVoidAuthorization,
                  data: {
                    additionalData: deactivationData.additionalData,
                  },
                },
              ];
              return voidTransaction(brmInputs);
            }
          } else if (err.code === CannotVoidTransactionWithTenderLines) {
            popupMessage(
              new EmeraldErrorModal({
                message: CannotVoidTransactionWithTenderLines,
                type: EmeraldDialogTypes.Error,
                hideCancelButton: true,
              }),
            );
          } else {
            popupMessage(err);
          }
        },
      );
  }
};

export const voidEmptyTransaction = async (showSpinner: boolean = true): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;
  const itemList = store.getState().itemList;
  const customer = store.getState().transaction.transactionCustomer;
  const areAllVoided = itemList.ejItems.every(item => {
    return item.Voided;
  });
  const isEmptyOrVoided = itemList.ejItems.length === 0 || areAllVoided;
  if (transactionId && isEmptyOrVoided && !customer) {
    const autoVoid = true;
    if (showSpinner) {
      await withSpinner(
        async (): Promise<void> => {
          await transactionCancel(transactionId, autoVoid).catch(err => {
            XLRLogger.error({
              message: `VoidEmptyTransaction failed:${err}`,
              source: 'void-transaction.ts',
            });
          });
        },
      );
    } else {
      await transactionCancel(transactionId, autoVoid).catch(err => {
        XLRLogger.error({
          message: `VoidEmptyTransaction failed:${err}`,
          source: 'void-transaction.ts',
        });
      });
    }
    await transactionEnd();
  }
};

export const voidTransactionOnPromotionDown = () => {
  if (voidTransactionWhenUEOffline.getIsVoid()) {
    voidActiveTransaction();
    voidTransactionWhenUEOffline.setIsVoid(false);
  }
};

const voidTransactionOnOffline = (): IVoidTransactionOffline => {
  let isVoid = false;
  const setIsVoid = value => {
    isVoid = value;
  };
  const getIsVoid = () => {
    return isVoid;
  };
  return { setIsVoid, getIsVoid };
};
export const voidTransactionWhenUEOffline = voidTransactionOnOffline();
interface IVoidTransactionOffline {
  setIsVoid: (value) => void;
  getIsVoid: () => boolean;
}
